import React from "react";
import styled from "styled-components";
import { SectionH2, GradientWord, Eyebrow } from "../components/Headlines";
import { projectsTmp, testimonialsData } from "../data";

const StyledWork = styled.section`
  background-color: ${(props) => props.theme.darkBg1};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 30px;

  @media screen and (min-width: 800px) {
    padding: 120px 60px;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 720px;
  margin-bottom: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
`;

const Grid = styled.div`
  width: 100%;
  max-width: 1000px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media screen and (min-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ClientCard = styled.figure`
  display: flex;
  flex-direction: column;
  margin: 0;
  background: ${(props) => props.theme.panel};
  border: 1px solid ${(props) => props.theme.hairline};
  border-radius: 20px;
  overflow: hidden;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: ${(props) => props.theme.accentBorder};
  }
`;

const Shot = styled.a`
  display: block;
  height: 180px;
  background-image: url(${(props) => props.$img});
  background-size: cover;
  background-position: top center;
  border-bottom: 1px solid ${(props) => props.theme.hairline};
`;

const Body = styled.figcaption`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 28px 30px 30px;
  flex-grow: 1;
`;

const ProjectRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProjectName = styled.span`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const ProjectTag = styled.span`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 13px;
  color: ${(props) => props.theme.textMuted};
`;

const VisitLink = styled.a`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 14px;
  color: ${(props) => props.theme.accent};
  white-space: nowrap;
`;

const Quote = styled.blockquote`
  font-family: ${(props) => props.theme.regularFont};
  font-weight: 400;
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
  border-top: 1px solid ${(props) => props.theme.hairline};
  padding-top: 20px;

  &::before {
    content: "\\201C";
    color: ${(props) => props.theme.accent};
    font-size: 24px;
    line-height: 0;
    margin-right: 2px;
  }
`;

const Author = styled.span`
  font-family: ${(props) => props.theme.regularFont};
  font-size: 14px;
  color: ${(props) => props.theme.textMuted};
`;

const MoreWork = styled.div`
  margin-top: 36px;
  font-family: ${(props) => props.theme.lightFont};
  font-size: 15px;
  color: ${(props) => props.theme.textMuted};

  a {
    color: rgba(255, 255, 255, 0.85);
  }

  a:hover {
    color: ${(props) => props.theme.accent};
  }
`;

const quoteFor = (name) => testimonialsData.find((t) => t.company === name);

export default function Work() {
  const featured = projectsTmp.filter((p) => quoteFor(p.name));
  const others = projectsTmp.filter((p) => !quoteFor(p.name));

  return (
    <StyledWork id="projects">
      <Header>
        <Eyebrow>Our work</Eyebrow>
        <SectionH2>
          Don't take <GradientWord>our word</GradientWord> for it
        </SectionH2>
      </Header>

      <Grid>
        {featured.map((p) => {
          const t = quoteFor(p.name);
          return (
            <ClientCard key={p.id}>
              <Shot
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                $img={p.img}
                aria-label={`Visit ${p.name}`}
              />
              <Body>
                <ProjectRow>
                  <ProjectMeta>
                    <ProjectName>{p.name}</ProjectName>
                    <ProjectTag>{p.description}</ProjectTag>
                  </ProjectMeta>
                  <VisitLink href={p.link} target="_blank" rel="noopener noreferrer">
                    Visit site ↗
                  </VisitLink>
                </ProjectRow>
                {t && (
                  <>
                    <Quote>{t.text}</Quote>
                    <Author>
                      {t.name} - {t.company}
                    </Author>
                  </>
                )}
              </Body>
            </ClientCard>
          );
        })}
      </Grid>

      {others.length > 0 && (
        <MoreWork>
          Also working with{" "}
          {others.map((p, i) => (
            <React.Fragment key={p.id}>
              <a href={p.link} target="_blank" rel="noopener noreferrer">
                {p.name}
              </a>
              {i < others.length - 1 ? ", " : ""}
            </React.Fragment>
          ))}
          .
        </MoreWork>
      )}
    </StyledWork>
  );
}
